import { useEffect, useRef, useState } from 'react'

import { ionHistory } from '../../navigation'
import { TOUR_MAP_HASH, TOUR_MAP_PARAM } from '../tourMapAddress'

// Which of the two URL forms this modal is addressed by. The grammar itself lives in
// `../tourMapAddress`, because the analytics that log a map view need it too and cannot import from
// here without closing a cycle.
//
// The pager has no stop in its URL and keeps the hash it has always used. The single page layout's hash
// is already the stop the reader has open, so a map that took it would collapse the row on the way in
// and not put it back — a query parameter is orthogonal, and there both survive the other.
export type TourMapAddress = 'hash' | 'query'

export { TOUR_MAP_HASH, TOUR_MAP_PARAM } from '../tourMapAddress'

// How long a page arriving with the map addressed is given before the modal opens. There is no callback
// for "the page has finished laying out", and a map built into a page still settling under it measures
// the wrong box.
const OPEN_BY_HASH_DELAY = 3000

const isAddressed = (addressBy: TourMapAddress): boolean => {
	const { hash, search } = ionHistory.location

	return addressBy === 'hash' ? hash === TOUR_MAP_HASH : new URLSearchParams(search).has(TOUR_MAP_PARAM)
}

const withoutMapParam = (search: string): string => {
	const params = new URLSearchParams(search)
	params.delete(TOUR_MAP_PARAM)
	const query = params.toString()

	return query ? `?${query}` : ''
}

const withMapParam = (search: string): string => {
	const params = new URLSearchParams(search)
	params.set(TOUR_MAP_PARAM, 'true')

	return `?${params.toString()}`
}

export interface TourMapModalOptions {
	// Nothing to open without a map.
	hasMap?: boolean
	// The map-first layout draws its map into the page, so a modal is not how that layout shows one — and
	// it addresses nothing, because the page itself is the map.
	isMapLayout?: boolean
	// A page still loading, or one that turned out to hold nothing, has no map worth opening yet.
	loading?: boolean
	isEmpty?: boolean
	addressBy?: TourMapAddress
}

export interface TourMapModal {
	isOpen: boolean
	// Whether the modal has finished presenting. The map inside it is mounted off this rather than off
	// `isOpen`, so it measures itself against a box that has stopped moving.
	didPresent: boolean
	// True while an arrival with the map addressed is waiting for the page to settle, so the page can show
	// it is going somewhere instead of appearing to ignore the link.
	isOpening: boolean
	toggle: () => void
	onDidPresent: (status: boolean) => void
}

/**
 * The map modal a tour opens over itself, and the URL that addresses it.
 *
 * Shared because two layouts show the same modal from different components, and a second copy would be
 * two sets of guards to keep in step. The guards are the part worth having in one place: each of them is
 * a page state in which a map either cannot be built or should not appear.
 */
export default function useTourMapModal({
	hasMap,
	isMapLayout,
	loading,
	isEmpty,
	addressBy = 'hash',
}: TourMapModalOptions): TourMapModal {
	const [isOpen, setIsOpen] = useState(false)
	const [didPresent, setDidPresent] = useState(false)
	const [isOpening, setIsOpening] = useState(false)
	// Whether the arrival has been answered. Set the moment it is acted on, and by presenting or
	// dismissing too: a reader who has closed the modal does not want the URL reopening it.
	const [arrivalAnswered, setArrivalAnswered] = useState(false)

	const openTimer = useRef<ReturnType<typeof setTimeout>>(undefined)

	useEffect(() => {
		if (arrivalAnswered || !isAddressed(addressBy) || loading || isEmpty || isOpen || !hasMap || isMapLayout) {
			return
		}

		setArrivalAnswered(true)
		setIsOpening(true)

		openTimer.current = setTimeout(() => {
			setIsOpen(true)
			setIsOpening(false)
		}, OPEN_BY_HASH_DELAY)
	}, [arrivalAnswered, addressBy, loading, isEmpty, isOpen, hasMap, isMapLayout])

	// The wait is long enough for the reader to leave inside it.
	useEffect(() => () => clearTimeout(openTimer.current), [])

	useEffect(() => {
		let wasAddressed = isAddressed(addressBy)

		return ionHistory.listen(() => {
			const nowAddressed = isAddressed(addressBy)

			// The back button, popping the entry that opening the modal pushed.
			if (wasAddressed && !nowAddressed) {
				setIsOpen(false)
			}

			wasAddressed = nowAddressed
		})
	}, [addressBy])

	const toggle = () => setIsOpen(open => !open)

	const onDidPresent = (status: boolean) => {
		setArrivalAnswered(true)
		setDidPresent(status)

		const { pathname, search, hash } = ionHistory.location

		if (!status) {
			setIsOpen(false)
			// The hash form replaces with the bare path, which drops the query string with it. Left as it
			// was found rather than fixed under a move. The query form takes out only the map: everything
			// either side of it is somebody else's — the hash is the stop the reader has open.
			ionHistory.replace(addressBy === 'hash' ? pathname : `${pathname}${withoutMapParam(search)}${hash}`)
			return
		}

		if (isMapLayout) {
			return
		}

		if (addressBy === 'hash') {
			window.location.hash = TOUR_MAP_HASH
			return
		}

		// Push, not replace: the entry is what the back button pops to close the modal again. The hash
		// rides along untouched, because on this layout it is the stop the reader has open.
		ionHistory.push(`${pathname}${withMapParam(search)}${hash}`)
	}

	return { isOpen, didPresent, isOpening, toggle, onDidPresent }
}
